import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  LinearProgress,
} from '@mui/material';
import {
  Psychology,
  Api,
  CheckCircle,
  Error,
  Visibility,
  VisibilityOff,
  Settings,
  Speed,
  Memory,
  Science,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../services/api';
import { LoggerService } from '../services/LoggerService';

interface AIProvider {
  id: string;
  name: string;
  type: 'openai' | 'anthropic' | 'azure' | 'local';
  api_key?: string;
  api_url: string;
  is_enabled: boolean;
  is_configured: boolean;
  last_updated?: string;
  status: 'active' | 'inactive' | 'error' | 'testing';
  description: string;
  max_tokens: number;
  temperature: number;
  supported_models: string[];
  capabilities: string[];
  cost_per_token: number;
  rate_limit: number;
  timeout_seconds: number;
}

interface AIConfiguration {
  id: string;
  provider_id: string;
  feature_type: 'regulatory_analysis' | 'risk_assessment' | 'document_parsing' | 'compliance_prediction';
  model_name: string;
  prompt_template: string;
  temperature: number;
  max_tokens: number;
  is_active: boolean;
  confidence_threshold: number;
  fallback_provider_id?: string;
}

interface ModelPerformanceMetrics {
  accuracy: number;
  response_time_ms: number;
  cost_per_request: number;
  success_rate: number;
  last_tested: string;
}

const AIConfigPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [activeTab, setActiveTab] = useState(0);
  const [providers, setProviders] = useState<AIProvider[]>([]);
  const [configurations, setConfigurations] = useState<AIConfiguration[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [showApiKeys, setShowApiKeys] = useState<{ [key: string]: boolean }>({});
  const [testResults, setTestResults] = useState<{ [key: string]: any }>({});
  const [performanceMetrics, setPerformanceMetrics] = useState<{ [key: string]: ModelPerformanceMetrics }>({});
  
  // Dialog states
  const [configDialog, setConfigDialog] = useState<{ open: boolean; provider: AIProvider | null }>({
    open: false,
    provider: null,
  });
  const [_testDialog, _setTestDialog] = useState<{ open: boolean; provider: AIProvider | null }>({
    open: false,
    provider: null,
  });

  useEffect(() => {
    loadAIConfiguration();
  }, []);

  const loadAIConfiguration = async () => {
    try {
      setLoading(true);
      const [providersRes, configurationsRes, metricsRes] = await Promise.all([
        api.get('/ai-ml/providers'),
        api.get('/ai-ml/configurations'),
        api.get('/ai-ml/performance-metrics')
      ]).catch(() => {
        // Fallback to mock data for development
        return [
          { data: getMockProviders() },
          { data: getMockConfigurations() },
          { data: getMockMetrics() }
        ];
      });

      setProviders(providersRes.data);
      setConfigurations(configurationsRes.data);
      setPerformanceMetrics(metricsRes.data);
    } catch (error: any) {
      LoggerService.error('Error loading AI configuration:', error);
      enqueueSnackbar('Failed to load AI configuration', { variant: 'error' });
      
      // Use mock data
      setProviders(getMockProviders());
      setConfigurations(getMockConfigurations());
      setPerformanceMetrics(getMockMetrics());
    } finally {
      setLoading(false);
    }
  };

  const getMockProviders = (): AIProvider[] => [
    {
      id: 'openai-gpt4',
      name: 'OpenAI GPT-4',
      type: 'openai',
      api_url: 'https://api.openai.com/v1',
      is_enabled: false,
      is_configured: false,
      status: 'inactive',
      description: 'OpenAI GPT-4 for advanced regulatory analysis and compliance predictions',
      max_tokens: 128000,
      temperature: 0.1,
      supported_models: ['gpt-4', 'gpt-4-turbo', 'gpt-4o'],
      capabilities: ['regulatory_analysis', 'document_parsing', 'compliance_prediction', 'risk_assessment'],
      cost_per_token: 0.00003,
      rate_limit: 10000,
      timeout_seconds: 30,
    },
    {
      id: 'openai-gpt35',
      name: 'OpenAI GPT-3.5 Turbo',
      type: 'openai',
      api_url: 'https://api.openai.com/v1',
      is_enabled: false,
      is_configured: false,
      status: 'inactive',
      description: 'Cost-effective GPT-3.5 for basic regulatory monitoring and document processing',
      max_tokens: 16384,
      temperature: 0.1,
      supported_models: ['gpt-3.5-turbo', 'gpt-3.5-turbo-16k'],
      capabilities: ['document_parsing', 'basic_analysis'],
      cost_per_token: 0.0000015,
      rate_limit: 20000,
      timeout_seconds: 15,
    },
    {
      id: 'azure-openai',
      name: 'Azure OpenAI Service',
      type: 'azure',
      api_url: 'https://your-resource.openai.azure.com',
      is_enabled: false,
      is_configured: false,
      status: 'inactive',
      description: 'Enterprise-grade Azure OpenAI with enhanced security and compliance',
      max_tokens: 128000,
      temperature: 0.1,
      supported_models: ['gpt-4', 'gpt-35-turbo'],
      capabilities: ['regulatory_analysis', 'document_parsing', 'compliance_prediction', 'risk_assessment'],
      cost_per_token: 0.00003,
      rate_limit: 5000,
      timeout_seconds: 45,
    }
  ];

  const getMockConfigurations = (): AIConfiguration[] => [
    {
      id: 'reg-analysis-config',
      provider_id: 'openai-gpt4',
      feature_type: 'regulatory_analysis',
      model_name: 'gpt-4',
      prompt_template: 'Analyze the following regulatory document for compliance implications in Luxembourg fund management context: {document}',
      temperature: 0.1,
      max_tokens: 2000,
      is_active: false,
      confidence_threshold: 0.85,
      fallback_provider_id: 'openai-gpt35',
    },
    {
      id: 'risk-assessment-config',
      provider_id: 'openai-gpt4',
      feature_type: 'risk_assessment',
      model_name: 'gpt-4',
      prompt_template: 'Assess the risk implications of the following scenario for fund management: {scenario}',
      temperature: 0.2,
      max_tokens: 1500,
      is_active: false,
      confidence_threshold: 0.80,
    }
  ];

  const getMockMetrics = (): { [key: string]: ModelPerformanceMetrics } => ({
    'openai-gpt4': {
      accuracy: 0.92,
      response_time_ms: 2500,
      cost_per_request: 0.045,
      success_rate: 0.98,
      last_tested: new Date().toISOString(),
    },
    'openai-gpt35': {
      accuracy: 0.85,
      response_time_ms: 1200,
      cost_per_request: 0.012,
      success_rate: 0.96,
      last_tested: new Date().toISOString(),
    }
  });

  const handleSaveProvider = async (provider: AIProvider) => {
    try {
      setSaving(true);
      await api.put(`/ai-ml/providers/${provider.id}`, provider);
      enqueueSnackbar('AI provider configuration saved successfully', { variant: 'success' });
      setConfigDialog({ open: false, provider: null });
      loadAIConfiguration();
    } catch (error: any) {
      LoggerService.error('Error saving provider:', error);
      enqueueSnackbar('Failed to save AI provider configuration', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestProvider = async (provider: AIProvider) => {
    try {
      setTesting(provider.id);
      const response = await api.post(`/ai-ml/providers/${provider.id}/test`, {
        test_prompt: 'Test regulatory analysis: What are the key compliance requirements for UCITS funds in Luxembourg?'
      });
      
      setTestResults(prev => ({
        ...prev,
        [provider.id]: response.data
      }));
      
      enqueueSnackbar('AI provider test completed successfully', { variant: 'success' });
    } catch (error: any) {
      LoggerService.error('Error testing provider:', error);
      enqueueSnackbar('AI provider test failed', { variant: 'error' });
      
      // Mock test result for development
      setTestResults(prev => ({
        ...prev,
        [provider.id]: {
          success: false,
          error: 'API key not configured or invalid',
          response_time: 0,
          test_response: null
        }
      }));
    } finally {
      setTesting(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'testing': return 'info';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  const getCapabilityChipColor = (capability: string) => {
    switch (capability) {
      case 'regulatory_analysis': return 'primary';
      case 'risk_assessment': return 'warning';
      case 'document_parsing': return 'info';
      case 'compliance_prediction': return 'success';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading AI Configuration...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <Psychology sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          AI Configuration
        </Typography>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="AI Providers" icon={<Api />} />
          <Tab label="Model Configurations" icon={<Settings />} />
          <Tab label="Performance Metrics" icon={<Speed />} />
          <Tab label="Usage & Billing" icon={<Memory />} />
        </Tabs>
      </Paper>

      {/* AI Providers Tab */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          {providers.map((provider) => (
            <Grid item xs={12} md={6} lg={4} key={provider.id}>
              <Card>
                <CardHeader
                  title={provider.name}
                  subheader={provider.description}
                  action={
                    <Chip
                      label={provider.status}
                      color={getStatusColor(provider.status) as any}
                      size="small"
                    />
                  }
                />
                <CardContent>
                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Capabilities
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {provider.capabilities.map((capability) => (
                        <Chip
                          key={capability}
                          label={capability.replace('_', ' ')}
                          color={getCapabilityChipColor(capability) as any}
                          size="small"
                        />
                      ))}
                    </Box>
                  </Box>

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">
                        Max Tokens
                      </Typography>
                      <Typography variant="body2">
                        {provider.max_tokens.toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">
                        Cost/Token
                      </Typography>
                      <Typography variant="body2">
                        ${provider.cost_per_token.toFixed(6)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">
                        Rate Limit
                      </Typography>
                      <Typography variant="body2">
                        {provider.rate_limit}/hour
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">
                        Configured
                      </Typography>
                      <Typography variant="body2">
                        {provider.is_configured ? (
                          <CheckCircle color="success" fontSize="small" />
                        ) : (
                          <Error color="error" fontSize="small" />
                        )}
                      </Typography>
                    </Grid>
                  </Grid>

                  {testResults[provider.id] && (
                    <Alert
                      severity={testResults[provider.id].success ? 'success' : 'error'}
                      sx={{ mb: 2 }}
                    >
                      {testResults[provider.id].success
                        ? `Test successful (${testResults[provider.id].response_time}ms)`
                        : `Test failed: ${testResults[provider.id].error}`
                      }
                    </Alert>
                  )}

                  <Box display="flex" gap={1}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setConfigDialog({ open: true, provider })}
                      startIcon={<Settings />}
                    >
                      Configure
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleTestProvider(provider)}
                      disabled={!provider.is_configured || testing === provider.id}
                      startIcon={testing === provider.id ? <CircularProgress size={16} /> : <Science />}
                    >
                      Test
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Model Configurations Tab */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          {configurations.map((config) => {
            const provider = providers.find(p => p.id === config.provider_id);
            return (
              <Grid item xs={12} lg={6} key={config.id}>
                <Card>
                  <CardHeader
                    title={config.feature_type.replace('_', ' ').toUpperCase()}
                    subheader={`Model: ${config.model_name} | Provider: ${provider?.name || 'Unknown'}`}
                    action={
                      <FormControlLabel
                        control={
                          <Switch
                            checked={config.is_active}
                            onChange={(e) => {
                              // Handle configuration toggle
                              LoggerService.debug('Toggle config:', config.id, e.target.checked);
                            }}
                          />
                        }
                        label="Active"
                      />
                    }
                  />
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="textSecondary">
                          Temperature
                        </Typography>
                        <Typography variant="body2">
                          {config.temperature}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="textSecondary">
                          Max Tokens
                        </Typography>
                        <Typography variant="body2">
                          {config.max_tokens}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="textSecondary">
                          Confidence Threshold
                        </Typography>
                        <Typography variant="body2">
                          {(config.confidence_threshold * 100).toFixed(0)}%
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="textSecondary">
                          Prompt Template Preview
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            backgroundColor: 'background.default',
                            p: 1,
                            borderRadius: 1,
                            fontSize: '0.8rem',
                            maxHeight: 100,
                            overflow: 'hidden'
                          }}
                        >
                          {config.prompt_template.substring(0, 150)}
                          {config.prompt_template.length > 150 ? '...' : ''}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Performance Metrics Tab */}
      {activeTab === 2 && (
        <Grid container spacing={3}>
          {Object.entries(performanceMetrics).map(([providerId, metrics]) => {
            const provider = providers.find(p => p.id === providerId);
            if (!provider) return null;

            return (
              <Grid item xs={12} md={6} key={providerId}>
                <Card>
                  <CardHeader
                    title={provider.name}
                    subheader={`Last tested: ${new Date(metrics.last_tested).toLocaleDateString()}`}
                  />
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="textSecondary">
                          Accuracy
                        </Typography>
                        <Typography variant="h6" color="primary">
                          {(metrics.accuracy * 100).toFixed(1)}%
                        </Typography>
                        <LinearProgress 
                          variant="determinate" 
                          value={metrics.accuracy * 100} 
                          sx={{ mt: 1 }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="textSecondary">
                          Success Rate
                        </Typography>
                        <Typography variant="h6" color="success.main">
                          {(metrics.success_rate * 100).toFixed(1)}%
                        </Typography>
                        <LinearProgress 
                          variant="determinate" 
                          value={metrics.success_rate * 100}
                          color="success"
                          sx={{ mt: 1 }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="textSecondary">
                          Avg Response Time
                        </Typography>
                        <Typography variant="body1">
                          {metrics.response_time_ms}ms
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="textSecondary">
                          Cost per Request
                        </Typography>
                        <Typography variant="body1">
                          ${metrics.cost_per_request.toFixed(3)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Usage & Billing Tab */}
      {activeTab === 3 && (
        <Card>
          <CardHeader title="AI Usage Statistics" />
          <CardContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              Detailed usage and billing metrics will be available once AI providers are configured and active.
            </Alert>
            <Typography variant="body2" color="textSecondary">
              This section will show:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
                <ListItemText primary="Monthly token usage by provider" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
                <ListItemText primary="Cost breakdown by feature type" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
                <ListItemText primary="Usage trends and forecasting" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
                <ListItemText primary="Rate limit monitoring" />
              </ListItem>
            </List>
          </CardContent>
        </Card>
      )}

      {/* Configuration Dialog */}
      <Dialog
        open={configDialog.open}
        onClose={() => setConfigDialog({ open: false, provider: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Configure AI Provider</DialogTitle>
        <DialogContent>
          {configDialog.provider && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="API Key"
                    type={showApiKeys[configDialog.provider.id] ? 'text' : 'password'}
                    value={configDialog.provider.api_key || ''}
                    onChange={(e) => setConfigDialog({
                      ...configDialog,
                      provider: { ...configDialog.provider!, api_key: e.target.value }
                    })}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={() => setShowApiKeys(prev => ({
                            ...prev,
                            [configDialog.provider!.id]: !prev[configDialog.provider!.id]
                          }))}
                        >
                          {showApiKeys[configDialog.provider.id] ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="API URL"
                    value={configDialog.provider.api_url}
                    onChange={(e) => setConfigDialog({
                      ...configDialog,
                      provider: { ...configDialog.provider!, api_url: e.target.value }
                    })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Max Tokens"
                    type="number"
                    value={configDialog.provider.max_tokens}
                    onChange={(e) => setConfigDialog({
                      ...configDialog,
                      provider: { ...configDialog.provider!, max_tokens: parseInt(e.target.value) }
                    })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Temperature"
                    type="number"
                    inputProps={{ min: 0, max: 2, step: 0.1 }}
                    value={configDialog.provider.temperature}
                    onChange={(e) => setConfigDialog({
                      ...configDialog,
                      provider: { ...configDialog.provider!, temperature: parseFloat(e.target.value) }
                    })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Rate Limit (requests/hour)"
                    type="number"
                    value={configDialog.provider.rate_limit}
                    onChange={(e) => setConfigDialog({
                      ...configDialog,
                      provider: { ...configDialog.provider!, rate_limit: parseInt(e.target.value) }
                    })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Timeout (seconds)"
                    type="number"
                    value={configDialog.provider.timeout_seconds}
                    onChange={(e) => setConfigDialog({
                      ...configDialog,
                      provider: { ...configDialog.provider!, timeout_seconds: parseInt(e.target.value) }
                    })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={configDialog.provider.is_enabled}
                        onChange={(e) => setConfigDialog({
                          ...configDialog,
                          provider: { ...configDialog.provider!, is_enabled: e.target.checked }
                        })}
                      />
                    }
                    label="Enable this provider"
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigDialog({ open: false, provider: null })}>
            Cancel
          </Button>
          <Button 
            onClick={() => configDialog.provider && handleSaveProvider(configDialog.provider)}
            variant="contained"
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : 'Save Configuration'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AIConfigPage;